/**
 * Periods —— 會計期間／月結鎖帳（P1-9）
 * 權限：founder 或 accountant 可以鎖／解鎖（`decisions/005`）。鎖咗之後該月唔准新增／沖銷。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopBar from '../components/TopBar';
import { api, type Me, type Meta } from '../lib/api';

interface Row { period: string; entity_id: string; status: string; locked_at: string | null }

export default function Periods({ meta, me }: { meta: Meta; me: Me }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [audit, setAudit] = useState<string | null>(null);

  const canLock = me.role === 'founder' || me.role === 'accountant';

  const load = useCallback(() => {
    api.periods(entityId).then((r) => setRows(r.periods)).catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (period: string, status: string) => {
    setBusy(true); setErr(null);
    try {
      await api.setPeriod(entityId, period, status === 'locked' ? 'unlock' : 'lock');
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setAudit(null); setErr(null);
    try {
      const r = await api.auditVerify();
      setAudit(r.ok ? t('periods.audit_ok', { n: r.count ?? 0 }) : t('periods.audit_bad', { at: r.brokenAt ?? '', reason: r.reason ?? '' }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <main className="page">
      <TopBar title={t('periods.heading')} />

      <section className="card">
        <label className="field">
          <span>{t('expense.entity')}</span>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
          </select>
        </label>
        {err && <p className="err">{err}</p>}
        {rows.length === 0 && <p className="note">{t('periods.empty')}</p>}
        <table className="ledger">
          <thead>
            <tr><th>{t('list.month')}</th><th>{t('periods.status')}</th><th>{t('periods.locked_at')}</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.period}>
                <td>{r.period}</td>
                <td>
                  <span className={`tag ${r.status === 'locked' ? 'warn' : ''}`}>
                    {r.status === 'locked' ? t('periods.locked') : t('periods.open')}
                  </span>
                </td>
                <td>{r.locked_at ?? '—'}</td>
                <td className="num">
                  {canLock && (
                    <button onClick={() => void toggle(r.period, r.status)} disabled={busy}>
                      {r.status === 'locked' ? t('periods.unlock') : t('periods.lock')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="note">{t('periods.note')}</p>
      </section>

      {me.role === 'founder' && (
        <section className="card">
          <h2>{t('periods.audit_heading')}</h2>
          <button onClick={() => void verify()} disabled={busy}>{t('periods.audit_cta')}</button>
          {audit && <p className={audit.includes('✅') ? 'ok' : 'err'}>{audit}</p>}
          <p className="note">{t('periods.audit_note')}</p>
        </section>
      )}
    </main>
  );
}
